import type { Customer, OrderCustomer } from 'src/types/types';

import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Iconify } from 'src/components/iconify';

import { AddressListDialog } from '../address';

const INVOICE_FROM: Customer = {
  id: 123,
  name: 'Coveen Copu',
  phone: '21 96 23 42',
  address: 'Hollændervej 162, 5500 Middelfart',
  company: 'Melfar Serviceudlejning ApS',
  companyCvr: '46040686',
  primary: false,
};

interface Props {
  setCustomer: (customer: OrderCustomer) => void;
  customer?: OrderCustomer;
  customerId: number | null;
  setCustomerId: (id: number | null) => void;
}

export function InvoiceNewEditAddress({ setCustomer, customer, customerId, setCustomerId }: Props) {
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const addressTo = useBoolean();

  const updateField = (field: keyof OrderCustomer, value: string) => {
    setCustomer({ ...(customer ?? { name: '', phone: '' }), [field]: value });
  };

  return (
    <>
      <Stack
        divider={
          <Divider
            flexItem
            orientation={mdUp ? 'vertical' : 'horizontal'}
            sx={{ borderStyle: 'dashed' }}
          />
        }
        sx={{ p: 3, gap: { xs: 3, md: 5 }, flexDirection: { xs: 'column', md: 'row' } }}
      >
        <Stack sx={{ width: 1 }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: 'text.disabled', flexGrow: 1 }}>
              Fra:
            </Typography>
            <IconButton sx={{ visibility: 'hidden' }}>
              <Iconify icon="mingcute:add-line" />
            </IconButton>
          </Box>

          <Stack spacing={1}>
            <Typography variant="subtitle2">{INVOICE_FROM.name}</Typography>
            <Typography variant="body2">{`${INVOICE_FROM.company} (${INVOICE_FROM.companyCvr})`}</Typography>
            <Typography variant="body2">{INVOICE_FROM.address}</Typography>
            <Typography variant="body2"> {INVOICE_FROM.phone}</Typography>
          </Stack>
        </Stack>

        <Stack sx={{ width: 1 }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: 'text.disabled', flexGrow: 1 }}>
              Til:
            </Typography>

            <IconButton onClick={addressTo.onTrue}>
              <Iconify icon={customer ? 'solar:pen-bold' : 'mingcute:add-line'} />
            </IconButton>
          </Box>

          {customer && (
            <Stack spacing={1.5}>
              <TextField
                label="Navn"
                size="small"
                fullWidth
                value={customer.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
              <TextField
                label="Firma"
                size="small"
                fullWidth
                value={customer.company ?? ''}
                onChange={(e) => updateField('company', e.target.value)}
              />
              <TextField
                label="CVR"
                size="small"
                fullWidth
                value={customer.companyCvr ?? ''}
                onChange={(e) => updateField('companyCvr', e.target.value)}
              />
              <TextField
                label="Adresse"
                size="small"
                fullWidth
                value={customer.address ?? ''}
                onChange={(e) => updateField('address', e.target.value)}
              />
              <TextField
                label="Telefon"
                size="small"
                fullWidth
                value={customer.phone}
                onChange={(e) => updateField('phone', e.target.value)}
              />
            </Stack>
          )}
        </Stack>
      </Stack>

      <AddressListDialog
        title="Kunder"
        open={addressTo.value}
        onClose={addressTo.onFalse}
        selected={(selectedId: string) => customerId?.toString() === selectedId}
        onSelect={(c) => {
          if (!c) return;
          setCustomer({
            name: c.name,
            phone: c.phone,
            address: c.address,
            company: c.company,
            companyCvr: c.companyCvr,
          });
          setCustomerId(c.id);
        }}
      />
    </>
  );
}
